import { DefinedUseQueryResult, useMutation, UseMutationResult, useQuery, useQueryClient } from "@tanstack/react-query"
import axios, { AxiosError } from "axios"
import { API_URL, IApiResponse, IGlobalError } from "@/shared"
import { showNotification } from "@mantine/notifications"
import { QueryRiderParams, Rider, SaveRiderDto } from "../domain/rider.types"

export const RIDER_KEYS = { FIND_PAGINATED: "RIDER_FIND_PAGINATED" }

export const useRiders = (params: QueryRiderParams): DefinedUseQueryResult<IApiResponse<Rider>, AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: [RIDER_KEYS.FIND_PAGINATED, params],
    queryFn: async () => {
      const res = await axios.get<IApiResponse<Rider>>(`${API_URL}/logistics/riders`, { params })
      return res.data
    },
    initialData: { data: [], totalRecords: 0 },
    refetchOnWindowFocus: false
  })
}

/** Despachadores a los que se les puede asignar puntos: activos y sin los excluidos del despacho (ej. "Libre") */
export const useAssignableRiders = (): Rider[] => {
  const { data } = useRiders({ rows: 200, assignable: true })
  return data.data ?? []
}

export const useCreateRider = (): UseMutationResult<Rider, AxiosError<IGlobalError>, SaveRiderDto> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (riderData: SaveRiderDto) => {
      const res = await axios.post<Rider>(`${API_URL}/logistics/riders`, riderData)
      return res.data
    },
    onSuccess: () => {
      showNotification({ title: "Repartidor agregado", message: "El repartidor ha sido agregado exitosamente.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [RIDER_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al agregar repartidor", message: error.response?.data?.message || "No se pudo agregar el repartidor. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useUpdateRider = (): UseMutationResult<Rider, AxiosError<IGlobalError>, { id: string; riderData: SaveRiderDto }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, riderData }) => {
      const res = await axios.patch<Rider>(`${API_URL}/logistics/riders/${id}`, riderData)
      return res.data
    },
    onSuccess: () => {
      showNotification({ title: "Repartidor actualizado", message: "El repartidor ha sido actualizado exitosamente.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [RIDER_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al actualizar repartidor", message: error.response?.data?.message || "No se pudo actualizar el repartidor. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useDeactivateRider = (): UseMutationResult<{ message: string }, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<{ message: string }>(`${API_URL}/logistics/riders/${id}/deactivate`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Repartidor desactivado", message: data?.message || "El repartidor ha sido desactivado.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [RIDER_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al desactivar repartidor", message: error.response?.data?.message || "No se pudo desactivar el repartidor. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useActivateRider = (): UseMutationResult<{ message: string }, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<{ message: string }>(`${API_URL}/logistics/riders/${id}/activate`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Repartidor reactivado", message: data?.message || "El repartidor ha sido reactivado.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [RIDER_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al reactivar repartidor", message: error.response?.data?.message || "No se pudo reactivar el repartidor. Inténtelo nuevamente.", color: "red" })
    }
  })
}
